/**
 * @module lib/audience/gtm-manager
 */
import ee from "lib/eventemitter";
import logger from "utils/logger";
import { toArray, normalizeObjectStrings, setProp } from "utils/utils";
import { verifyEvent } from "./event-register";
import { gtmPackages } from "./gtm-packages";
import isFunction from "lodash/isFunction";
import isNil from "lodash/isNil";
import { getUrlVars } from "utils/urls";

const debugTag = "[GTM-MANAGER]";

/**
 * @method trackGTMEvent
 * @description takes in a param and pushes it into the analytics layer array
 * @param {Object} c - collection of params
 * @param {Object} c.data - object defined either in JS or HTML
 * @param {String} [c.event] - a string defining the type of event
 * @param {Function} [c.eventCallback] - callback function
 */
export function trackGTMEvent({ data, event, eventCallback }) {
  logger.groupCollapsed(`${debugTag} | ${data.type || "custom"}`);

  const defaultEvent = "clickEvent";

  // if event is defined, return value of event
  // if event is NOT defined, return value of `defaultEvent`
  let processedEvent = isNil(event) ? defaultEvent : event;
  let gtmObject = {};

  /* ======================================================================
   * TODO: This should be fixed because this pretty much breaks every thing
   * ie. let verifyEvt = verifyEvent(Object.keys(data), processedEvent, data.type ? data.type : null);
   * if data is not correct, as per, `event-register.js` a lot of analytics would not be sent
   * ====================================================================== */
  verifyEvent(Object.keys(data), processedEvent, data.type ? data.type : null);

  if (processedEvent === "clickEvent" && data.linkType) {
    if (data.linkType === "internal") {
      /* eslint-disable no-param-reassign */
      processedEvent = "nextPageClickEvent";
      /* eslint-enable no-param-reassign */
    }
    delete data.linkType;
  }
  gtmObject.event = processedEvent;
  gtmObject[processedEvent] = normalizeObjectStrings(data);
  logger.table(data);
  if (isFunction(eventCallback)) {
    gtmObject.eventCallback = eventCallback;
  }
  window.analyticsLayer && window.analyticsLayer.push(gtmObject);
  logger.groupEnd(`${debugTag} | ${data.type}`);
}

/**
 * @method handleClickEvent
 * @description event handling for elements that specify a [data-analyticsclick] attribute
 * @param {MouseEvent} event
 */
export function handleClickEvent(event) {
  if (!window.gtm) {
    // google tag manager is not on the page, so carry on
    return;
  }
  const click = (function click() {
    const src = event.target;
    const clicked = src.closest("[data-analyticsclick]");
    // https://developer.mozilla.org/en-US/docs/Web/Events/click - event.button (standard, zero-based)
    // https://developer.mozilla.org/en-US/docs/Web/API/MouseEvent - event.which (non-standard, one-based)
    const openInNewTab = (event.ctrlKey || event.shiftKey || event.metaKey) // meta is Command/Alt key
      || (event.button && (event.button == 1 || event.button == 2)) // Middle or right button click
      || (event.which && (event.which == 2 || event.which == 3)); // Middle or right button click
    const data = (() => {
      if (openInNewTab) {
        // In Chrome and Firefox users must unblock popups from theglobeandmail.com if they want
        // to open tracked clickEvent links in a new tab/window.
        // No click data is therefore returned here (a clickEvent with no data should not be tracked)
        // in order to preserve the default browser behaviour and not force users to accept
        // popups from us.
        return null;
      }
      const dataset = clicked && clicked.dataset && clicked.dataset.analyticsclick;
      const external = clicked && clicked.getAttribute("rel");
      let parsedData;
      if (!dataset) {
        return null;
      }
      try {
        parsedData = JSON.parse(dataset);
      } catch (e) {
        return null;
      }
      parsedData.linkType = external && external.indexOf("external") !== -1 ? "external" : "internal";
      if (isNil(parsedData.contentId) && !isNil(clicked.dataset.sophi)) {
        parsedData.contentId = clicked.dataset.sophi;
      }
      if (isNil(parsedData.page) && !isNil(clicked.dataset.ltPos)) {
        parsedData.page = clicked.dataset.ltPos;
      }
      // Dynamically determine the item's position within its container
      const container = clicked.closest("[data-analyticsclick-container]");
      if (container) {
        const items = toArray(container.querySelectorAll("[data-analyticsclick]"));
        if (isNil(parsedData.position)) {
          items.forEach((item, index) => {
            if (item === clicked) {
              parsedData.position = index + 1;
            }
          });
        }
      }

      // New Packages
      // check if clicked item is story card within a package
      const pkgData = gtmPackages(clicked);
      if (!isNil(pkgData)) {
        parsedData.presentation = pkgData.presentation;
        parsedData.variantName = pkgData.variantName;
      }
      // check if clicked item is story card within a top package
      const topPackage = clicked.closest(".c-package--top");
      if (topPackage) {
        let packageNumber = topPackage.dataset.package || 1;
        parsedData.feature = `package ${packageNumber}`;
        if (parsedData.page) {
          parsedData.page += `:package ${packageNumber}`;
        }
      }
      return parsedData;
    })();
    const href = clicked && clicked.hasAttribute("href") ? clicked.getAttribute("href") : undefined;
    const target = clicked && clicked.hasAttribute("target") ? clicked.getAttribute("target") : undefined;

    return {
      data,
      src,
      href,
      target
    };
  })();
  if (!click.data) {
    return;
  }
  event.preventDefault();

  let clickFollowed = false;
  function follow() {
    logger.info(`${debugTag} follow() called`);
    if (click.target) {
      const fenetre = window.open(click.href, click.target);
      setProp(fenetre, "opener", null);
    } else {
      if (click.href) {
        window.location = click.href;
      }
    }
    clickFollowed = true;
  }

  // TODO: investigate why Chrome complains: [Violation] 'click' handler took XXXms
  // trackGTMEvent should never be called directly
  // instead we should emit the appropriate event
  ee.trigger("analytics.trackGTMEvent", [click.data, "clickEvent", follow]);

  if (getUrlVars().nofollowtimeout) {
    return;
  }

  setTimeout(function followHref() {
    if (!clickFollowed) {
      follow();
    }
  }, 300);
}
